import type { ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import type { DataSlot } from './PulseChart.tsx';

export interface RingMeterProps {
  /** 0 to 1. */
  value: number;
  /** Diameter in px: 40 in a tile, 64 in a panel. */
  size?: number;
  /** A chart slot, or attention when the share is the thing a person must act on. */
  tone?: DataSlot | 'attention' | 'neutral';
  /** In the hole: a short value ("2/3"). */
  children?: ReactNode;
  /** Accessible summary: "2 of 3 machines online". */
  label: string;
  className?: string;
}

const STROKE: Record<'attention' | 'neutral', string> = {
  attention: 'var(--status-attention-fill)',
  neutral: 'var(--fg-muted)',
};

/** A ring that fills clockwise from the top: one share at a glance. It sweeps in once. */
export function RingMeter({ value, size = 40, tone = 1, children, label, className }: RingMeterProps) {
  const stroke = Math.max(3, Math.round(size / 10));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.min(1, Math.max(0, value));
  return (
    <span role="img" aria-label={label} className={cn('relative inline-flex shrink-0 items-center justify-center', className)} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-active)" strokeWidth={stroke} />
        {v > 0 && (
          <circle
            className="motion-sweep"
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={typeof tone === 'number' ? `var(--data-${tone})` : STROKE[tone]}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            style={{ strokeDashoffset: c * (1 - v), ['--ring-c' as string]: `${c}` }}
          />
        )}
      </svg>
      {children != null && <span className="absolute inset-0 flex items-center justify-center text-caption font-medium text-fg num">{children}</span>}
    </span>
  );
}
